export const PORTRAIT_LAYOUT = Object.freeze({
  canvasWidth: 605,
  canvasHeight: 688,
  guideY: 63,
  targetFaceCenterX: 605 / 2,
  targetFaceCenterY: 288,
  targetFaceSize: 205,
  maximumFaceSize: 340,
  minimumArtworkClearance: 8,
  artworkRight: 258,
  artworkY: 300,
});

function displayRect(box, placement) {
  if (!box) return null;
  const scale = placement.baseScale * placement.photoScale;
  const left = placement.photoX + (box.x - placement.imageWidth / 2) * scale;
  const top = placement.photoY + (box.y - placement.imageHeight / 2) * scale;
  return { left, top, right: left + box.width * scale, bottom: top + box.height * scale, width: box.width * scale, height: box.height * scale };
}

function validBox(box) {
  return Boolean(box?.width > 0 && box?.height > 0 && Number.isFinite(box.x) && Number.isFinite(box.y));
}

// MediaPipe can occasionally return zero face detections in production even
// though person segmentation succeeded. In that case use the cutout bounds as
// a conservative placement proxy so auto-positioning and artwork placement do
// not silently fall back to the raw defaults. A real face box always wins.
export function estimateFaceFromForeground(foreground, imageWidth, imageHeight) {
  if (!validBox(foreground) || !imageWidth || !imageHeight) return null;
  const width = Math.max(1, Math.min(
    foreground.width * 0.46,
    foreground.height * 0.38,
    imageWidth * 0.62,
  ));
  const height = Math.max(1, Math.min(
    foreground.height * 0.44,
    width * 1.12,
    imageHeight * 0.58,
  ));
  const centerX = foreground.x + foreground.width / 2;
  const topInset = Math.min(
    Math.max(0, foreground.height - height),
    foreground.height * 0.08,
  );
  return {
    x: centerX - width / 2,
    y: foreground.y + topInset,
    width,
    height,
  };
}

function resolvePlacementFace(face, foreground, imageWidth, imageHeight) {
  return validBox(face) ? face : estimateFaceFromForeground(foreground, imageWidth, imageHeight);
}

// Follow the face contour rather than treating the entire artwork bounding
// square as opaque. Include room below the detector box for the chin/beard.
export function artworkTouchesFace(rect, artwork) {
  if (!rect || !artwork?.size) return false;
  const rx = rect.width * 0.53, ry = rect.height * 0.60;
  const cx = (rect.left + rect.right) / 2, cy = rect.top + rect.height * 0.55;
  const ax = artwork.x + artwork.size / 2, ay = artwork.y + artwork.size / 2;
  if (((ax - cx) / rx) ** 2 + ((ay - cy) / ry) ** 2 <= 1) return true;
  const radius = artwork.size / 2 + PORTRAIT_LAYOUT.minimumArtworkClearance;
  for (let i = 0; i < 180; i++) {
    const angle = i * Math.PI / 90;
    if (Math.hypot(cx + rx * Math.cos(angle) - ax, cy + ry * Math.sin(angle) - ay) <= radius) return true;
  }
  return false;
}

function placeArtwork(faceRect, size, y = PORTRAIT_LAYOUT.artworkY) {
  // The revised reference moves the circle 20px up and left (38% right edge).
  // First lower the circle around a larger face, keeping that visible width;
  // only move it farther left if the lower-left area still has no room.
  const preferred = PORTRAIT_LAYOUT.artworkRight - size;
  const startY = y;
  while (y < startY + 96 && artworkTouchesFace(faceRect, { x: preferred, y, size })) y += 2;
  if (!artworkTouchesFace(faceRect, { x: preferred, y, size })) return { x: preferred, y };
  let left = -size - PORTRAIT_LAYOUT.minimumArtworkClearance, right = preferred;
  for (let i = 0; i < 24; i++) {
    const x = (left + right) / 2;
    if (artworkTouchesFace(faceRect, { x, y, size })) right = x;
    else left = x;
  }
  return { x: left, y };
}

export function calculateAutoPlacement({ imageWidth, imageHeight, baseScale, face, foreground, artwork }) {
  if (!imageWidth || !imageHeight || !baseScale) return null;
  const resolvedFace = resolvePlacementFace(face, foreground, imageWidth, imageHeight);
  if (!resolvedFace) return null;
  const layout = PORTRAIT_LAYOUT;
  const bounds = validBox(foreground) ? foreground : { x: 0, y: 0, width: imageWidth, height: imageHeight };
  const fx = resolvedFace.x + resolvedFace.width / 2, fy = resolvedFace.y + resolvedFace.height / 2;
  // Keep the top of the cutout close to the guide so the chest sits higher
  // in the finished portrait while retaining the chest-friendly framing.
  const headY = layout.guideY + 6;
  const faceSize = Math.max(resolvedFace.width, resolvedFace.height);
  // Solve coverage and headroom together, not independent zoom and capped
  // translations. Horizontal coverage concerns the source crop, not the normal
  // grey space beside the head. Never stretch or invent missing shoulders.
  // The portrait is a transparent cutout drawn over the generated grey
  // background, so the original source-image edges do not need to cover the
  // canvas. Scaling against those edges over-zooms off-centre source photos
  // and crops away useful jacket/chest area. Size from the face and vertical
  // subject extent instead, while still keeping the portrait grounded at the
  // bottom of the frame.
  const needed = Math.max(
    layout.targetFaceSize / faceSize,
    (layout.canvasHeight - headY + 2) / bounds.height,
  );
  const scale = Math.min(needed, layout.maximumFaceSize / faceSize);
  const minimumCenterY = headY + (fy - bounds.y) * scale;
  const maximumCenterY = layout.canvasHeight + 1 - (bounds.y + bounds.height - fy) * scale;
  // If the source is too tight to satisfy both, retain the whole head and
  // report the uncovered frame instead of approving it or zooming indefinitely.
  const centerY = Math.max(minimumCenterY, Math.min(layout.targetFaceCenterY, maximumCenterY));
  const placement = {
    imageWidth, imageHeight, baseScale,
    photoScale: scale / baseScale,
    photoX: layout.targetFaceCenterX - (fx - imageWidth / 2) * scale,
    photoY: centerY - (fy - imageHeight / 2) * scale,
  };
  const positionedArtwork = placeArtwork(displayRect(resolvedFace, placement), artwork?.size ?? 579.5);
  return { ...placement, artworkX: positionedArtwork.x, artworkY: positionedArtwork.y, estimatedFace: !validBox(face) };
}

export function assessPortraitPlacement({ imageWidth, imageHeight, baseScale, photoScale, photoX, photoY, face, foreground, artwork }) {
  const placement = { imageWidth, imageHeight, baseScale, photoScale, photoX, photoY };
  const frame = displayRect({ x: 0, y: 0, width: imageWidth, height: imageHeight }, placement);
  const subject = displayRect(foreground, placement);
  // The canvas background is generated independently, so transparent/raw
  // source-image side edges are valid. Only require the visible portrait to
  // reach the bottom edge; face framing handles the horizontal placement.
  const coverage = Boolean(subject && subject.bottom >= PORTRAIT_LAYOUT.canvasHeight - 2);
  const resolvedFace = resolvePlacementFace(face, foreground, imageWidth, imageHeight);
  if (!resolvedFace) return { detected: false, estimatedFace: false, headroom: false, artworkClear: false, framed: false, coverage, good: false, faceRect: null, message: 'Position the portrait manually', detail: 'Keep the whole head below the guide and check the bottom and sides.' };
  const faceRect = displayRect(resolvedFace, placement);
  const headTop = subject?.top ?? faceRect.top;
  const headroom = headTop >= PORTRAIT_LAYOUT.guideY + 3 && headTop <= PORTRAIT_LAYOUT.guideY + 70;
  const artworkClear = !artworkTouchesFace(faceRect, artwork);
  const centered = Math.abs((faceRect.left + faceRect.right) / 2 - PORTRAIT_LAYOUT.targetFaceCenterX) <= 24;
  const framed = centered && faceRect.left >= 12 && faceRect.right <= PORTRAIT_LAYOUT.canvasWidth - 12 && faceRect.bottom <= PORTRAIT_LAYOUT.canvasHeight - 24 && Math.max(faceRect.width, faceRect.height) >= 190 && Math.max(faceRect.width, faceRect.height) <= PORTRAIT_LAYOUT.maximumFaceSize + 1;
  const good = headroom && artworkClear && framed && coverage;
  const [message, detail] = !coverage
    ? ['Check the portrait bottom', 'Use Auto position. If a gap remains, move the portrait down slightly or choose a photo showing more of the chest.']
    : !headroom ? ['Check the headroom', 'Keep the whole head just below the thin guide bar.']
    : !artworkClear ? ['Move the circle away from the face', 'The turquoise artwork may cover clothing, but keep it clear of the face.']
    : !framed ? ['Check the face position', 'Center the face and keep its size natural.']
    : ['Portrait position looks good', 'The face is centered, the photo reaches the frame, and the artwork is clear.'];
  return { detected: true, estimatedFace: !validBox(face), headroom, artworkClear, framed, coverage, good, faceRect, message, detail };
}
